// GST rules for the Vendor Master form.
//
// A GSTIN is 15 characters and encodes its own data:
//   29ABCDE1234F1Z5
//    ^^^^^  ^^^^^^^^
//    state  PAN (chars 3-12)
// so Region and PAN can be derived from it instead of retyped. Both stay
// editable — this only pre-fills and warns, it never blocks a save.

// GST state code -> state / UT name. Codes 25, 28 and 97-99 are legacy or
// special-jurisdiction entries that still turn up on older GSTINs.
export const GST_STATE_CODES = {
  '01': 'Jammu and Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  '25': 'Daman and Diu',
  '26': 'Dadra and Nagar Haveli and Daman and Diu',
  '27': 'Maharashtra',
  '28': 'Andhra Pradesh',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman and Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh',
  '38': 'Ladakh',
  '97': 'Other Territory',
  '99': 'Centre Jurisdiction',
};

// 28 was Andhra Pradesh before the 2014 split; 37 is the successor.
const STATE_ALIASES = {
  '28': ['andhra pradesh', 'andhra'],
  '37': ['andhra pradesh', 'andhra'],
  '07': ['delhi', 'new delhi', 'ncr', 'national capital territory'],
  '19': ['west bengal', 'wb'],
  '29': ['karnataka', 'ka'],
  '33': ['tamil nadu', 'tn'],
  '32': ['kerala'],
  '27': ['maharashtra', 'mh'],
  '08': ['rajasthan', 'raj'],
  '23': ['madhya pradesh', 'mp'],
  '02': ['himachal pradesh', 'hp'],
  '06': ['haryana', 'hr'],
  '09': ['uttar pradesh', 'up'],
  '24': ['gujarat', 'gj'],
  '21': ['odisha', 'orissa'],
  '36': ['telangana', 'ts'],
  '38': ['ladakh'],
  '01': ['jammu and kashmir', 'j&k'],
  '03': ['punjab', 'pb'],
  '04': ['chandigarh', 'ch'],
  '05': ['uttarakhand', 'uk', 'uttaranchal'],
  '34': ['puducherry', 'pondicherry'],
};

// 97 and 99 are catch-all jurisdictions, not places. A free-text Region can
// never sensibly be compared against them, so stay quiet rather than cry wolf.
const NON_GEOGRAPHIC = ['97', '99'];

const clean = v => String(v ?? '').trim();

/** Uppercase, strip spaces and punctuation — users paste GSTINs in every shape. */
const normalise = v =>
  clean(v).toUpperCase().replace(/[\s\-_]/g, '');

export const isValidGstin = gstin => /^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/.test(normalise(gstin));

/** First two digits, or '' when there is not enough of a GSTIN yet. */
export const gstinStateCode = gstin => {
  const g = normalise(gstin);
  return /^\d{2}/.test(g) ? g.slice(0, 2) : '';
};

/** Characters 3-12 of the GSTIN, which are the PAN. */
export const gstinPan = gstin => {
  const g = normalise(gstin);
  return g.length >= 12 ? g.slice(2, 12) : '';
};

export const stateNameFor = code => GST_STATE_CODES[clean(code)] || '';

/** True when `code` could plausibly be the state behind the typed region. */
const codeAcceptsRegion = (code, region) => {
  const r = clean(region).toLowerCase();
  if (!r) return true;
  const accepted = STATE_ALIASES[code];
  if (accepted && accepted.some(a => r.includes(a))) return true;
  const name = GST_STATE_CODES[code];
  return name ? r.includes(name.toLowerCase()) : false;
};

/**
 * Fuzzy region-vs-GSTIN check: true when the typed region cannot be matched to
 * a *different* state, so a typo is tolerated but a genuine mismatch is caught.
 * Returns { ok, reason, expected } so callers can explain themselves.
 */
export const checkRegionAgainstGstin = (region, gstin) => {
  const code = gstinStateCode(gstin);
  if (!code) return { ok: true, reason: null, expected: '' };
  if (NON_GEOGRAPHIC.includes(code)) return { ok: true, reason: null, expected: '' };
  const expected = stateNameFor(code);
  if (!expected) return { ok: true, reason: null, expected: '' };
  if (codeAcceptsRegion(code, region)) return { ok: true, reason: null, expected };
  return {
    ok: false,
    expected,
    reason: `Region "${clean(region)}" does not look like ${expected}, which is state code ${code} in this GSTIN`,
  };
};

/** Rule 2: the PAN typed must be the PAN embedded in the GSTIN. */
export const checkPanAgainstGstin = (pan, gstin) => {
  const embedded = gstinPan(gstin);
  if (!embedded) return { ok: true, reason: null };
  const typed = normalise(pan);
  if (!typed) return { ok: true, reason: null };
  if (typed === embedded) return { ok: true, reason: null };
  return {
    ok: false,
    expected: embedded,
    reason: `PAN ${typed} does not match ${embedded}, which is the PAN inside this GSTIN`,
  };
};

/** Both rules at once, for a single summary message. */
export const checkGstConsistency = ({ region, pan, gstin }) => {
  const problems = [];
  if (!isValidGstin(gstin)) return problems;

  const r = checkRegionAgainstGstin(region, gstin);
  if (!r.ok) problems.push(r.reason);

  const p = checkPanAgainstGstin(pan, gstin);
  if (!p.ok) problems.push(p.reason);

  return problems;
};
